// ── Commerce API client ──────────────────────────────────────────
// Thin wrapper around the Digital Products API Commerce module
// (see http://localhost:8400 → Commerce).

import { request } from "@/lib/api/client";
import { cachedRequest, COMMERCE_CACHE_PREFIX } from "@/lib/api/cache";

/** Storefront product slug for the Nurse Pro subscription. */
export const PRO_SUBSCRIPTION_SLUG = "pro-subscription";

/** Storefront product slug for certificates (GET /v1/commerce/product/certificate). */
export const CERTIFICATE_PRODUCT_SLUG = "certificate";

/** Variant slug for the WIHRA membership certificate. */
export const MEMBERSHIP_CERTIFICATE_VARIANT_SLUG = "membership-certificate";

export type CommerceProductType = "subscription" | "certificate";

/** Survives the Stripe redirect in the same tab so checkout success/cancel can route back. */
const CHECKOUT_PRODUCT_TYPE_KEY = "nursetools_checkout_product_type";

/**
 * In-memory copy after takeCheckoutProductType(), so React Strict Mode remounts
 * do not lose the value after the first effect clears sessionStorage.
 * `undefined` = not taken yet for the current checkout.
 */
let takenCheckoutProductType: CommerceProductType | null | undefined;

/** Call immediately before sending the browser to the payment gateway. */
export function rememberCheckoutProductType(type: CommerceProductType): void {
  // Allow a later takeCheckoutProductType() to read this checkout, not a prior one.
  takenCheckoutProductType = undefined;
  try {
    window.sessionStorage.setItem(CHECKOUT_PRODUCT_TYPE_KEY, type);
  } catch (err) {
    console.error("Failed to remember checkout product type", err);
  }
}

export function peekCheckoutProductType(): CommerceProductType | null {
  try {
    const value = window.sessionStorage.getItem(CHECKOUT_PRODUCT_TYPE_KEY);
    if (value === "subscription" || value === "certificate") return value;
    return null;
  } catch (err) {
    console.error("Failed to read checkout product type", err);
    return null;
  }
}

export function clearCheckoutProductType(): void {
  try {
    window.sessionStorage.removeItem(CHECKOUT_PRODUCT_TYPE_KEY);
  } catch (err) {
    console.error("Failed to clear checkout product type", err);
  }
}

/** Read + clear once for the current checkout return screen. */
export function takeCheckoutProductType(): CommerceProductType | null {
  if (takenCheckoutProductType !== undefined) return takenCheckoutProductType;
  const value = peekCheckoutProductType();
  clearCheckoutProductType();
  takenCheckoutProductType = value;
  return value;
}

export interface ApiCommerceSubscriptionPlanLimit {
  limitKey: string;
  limitValue: number;
  period: string;
}

export interface ApiCommerceSubscriptionPlanFeature {
  featureKey: string;
  featureValue: number;
}

export interface ApiCommerceSubscriptionPlan {
  durationDays: number;
  billingMode: string;
  limits: ApiCommerceSubscriptionPlanLimit[];
  features: ApiCommerceSubscriptionPlanFeature[];
}

/** Variant from GET /v1/commerce/product/{slug} `variants[]`. */
export interface ApiCommerceProductVariant {
  id: number;
  slug: string;
  label: string;
  description: string | null;
  sortOrder: number;
  isPopular: boolean;
  amount: number;
  effectiveAmount: number;
  currency: string;
  discountEnabled: boolean;
  subscriptionPlan?: ApiCommerceSubscriptionPlan | null;
}

/**
 * Localized product from GET /v1/commerce/product `data[]`
 * and GET /v1/commerce/product/{slug}.
 */
export interface ApiCommerceProduct {
  slug: string;
  type: CommerceProductType;
  sortOrder: number;
  name: string;
  description: string | null;
  exampleImageUrl: string | null;
  variants: ApiCommerceProductVariant[];
}

/** Line item from POST /v1/commerce/order `data.lines[]`. */
export interface ApiCommerceOrderLine {
  id: number;
  variantId: number;
  quantity: number;
  unitPrice: number;
  discountApplied: number;
}

/** Pending checkout order from POST /v1/commerce/order (commerceOrderStore). */
export interface ApiCommerceOrder {
  id: number;
  userId: number | null;
  status: string;
  currency: string;
  subtotal: number;
  discount: number;
  total: number;
  promotionId: number | null;
  createdAt: string;
  updatedAt: string;
  lines: ApiCommerceOrderLine[];
  /**
   * Opaque ownership token for guest orders (jwt.optional create).
   * Present only on the create response for unauthenticated buyers.
   */
  checkoutToken?: string | null;
}

export type CommercePaymentProvider = "stripe" | "yookassa";

/**
 * Allowlisted attribution keys for POST /v1/commerce/order/{id}/pay `metadata`
 * (merged into Stripe Checkout Session metadata).
 */
export type CommercePaymentMetadataKey =
  | "utm_source"
  | "utm_medium"
  | "utm_campaign"
  | "utm_term"
  | "utm_content"
  | "gclid"
  | "fbclid";

export type CommercePaymentMetadata = Partial<
  Record<CommercePaymentMetadataKey, string>
>;

const COMMERCE_PAYMENT_METADATA_KEYS: readonly CommercePaymentMetadataKey[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
];

const COMMERCE_PAYMENT_METADATA_MAX_LENGTH = 500;

/** Drop empty values and unknown keys; truncate to API max length. */
export function sanitizeCommercePaymentMetadata(
  metadata: CommercePaymentMetadata | null | undefined,
): CommercePaymentMetadata | undefined {
  if (!metadata) return undefined;

  const cleaned: CommercePaymentMetadata = {};
  let found = false;

  for (const key of COMMERCE_PAYMENT_METADATA_KEYS) {
    const raw = metadata[key];
    if (typeof raw !== "string") continue;
    const value = raw.trim().slice(0, COMMERCE_PAYMENT_METADATA_MAX_LENGTH);
    if (!value) continue;
    cleaned[key] = value;
    found = true;
  }

  return found ? cleaned : undefined;
}

export interface ApiCommercePayment {
  id: number;
  provider: CommercePaymentProvider;
  providerPaymentId: string;
  mode: string;
  status: string;
}

/** Result from POST /v1/commerce/order/{id}/pay (commerceOrderPay). */
export interface ApiCommerceOrderPaymentResult {
  order: ApiCommerceOrder;
  payment: ApiCommercePayment;
  checkoutUrl: string | null;
  clientSecret: string | null;
}

/** Share in-flight GETs so React Strict Mode / remounts don't double-hit the API. */
const inflight = new Map<string, Promise<unknown>>();

function dedupeInflight<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;
  const promise = run().finally(() => {
    if (inflight.get(key) === promise) inflight.delete(key);
  });
  inflight.set(key, promise);
  return promise;
}

/** GET /v1/commerce/product — list active products (localized via Accept-Language). */
export async function fetchCommerceProducts(
  locale?: string,
): Promise<ApiCommerceProduct[]> {
  const key = `commerce-products:${locale ?? ""}`;
  return dedupeInflight(key, async () => {
    const data = await request<ApiCommerceProduct[]>("/v1/commerce/product", {
      method: "GET",
      headers: locale ? { "Accept-Language": locale } : undefined,
    });
    return data ?? [];
  });
}

/**
 * GET /v1/commerce/product/{slug} — show one active product (localized).
 *
 * Cached: the Pro modal refetches its prices on every open (it clears
 * `variant` on close), so without this each open cost a full round-trip
 * through the BFF proxy before the price could render.
 */
export async function fetchCommerceProduct(
  slug: string,
  locale?: string,
): Promise<ApiCommerceProduct> {
  const key = `${COMMERCE_CACHE_PREFIX}product:${locale ?? ""}:${slug}`;
  return cachedRequest(key, () =>
    request<ApiCommerceProduct>(`/v1/commerce/product/${encodeURIComponent(slug)}`, {
      method: "GET",
      headers: locale ? { "Accept-Language": locale } : undefined,
    }),
  );
}

/** Match order line variant IDs against known storefront products. */
export async function resolveOrderProductType(
  order: ApiCommerceOrder,
): Promise<CommerceProductType | null> {
  const variantIds = new Set(order.lines.map((line) => line.variantId));
  if (!variantIds.size) return null;

  try {
    const certificate = await fetchCommerceProduct(CERTIFICATE_PRODUCT_SLUG);
    if (certificate.variants.some((variant) => variantIds.has(variant.id))) {
      return "certificate";
    }
  } catch (err) {
    console.error("Failed to resolve certificate product for checkout order", err);
  }

  try {
    const subscription = await fetchCommerceProduct(PRO_SUBSCRIPTION_SLUG);
    if (subscription.variants.some((variant) => variantIds.has(variant.id))) {
      return "subscription";
    }
  } catch (err) {
    console.error(
      "Failed to resolve subscription product for checkout order",
      err,
    );
  }

  return null;
}

/** Prefer the popular variant, otherwise the lowest sortOrder. */
export function pickCommerceVariant(
  variants: ApiCommerceProductVariant[],
): ApiCommerceProductVariant | null {
  if (!variants.length) return null;
  const popular = variants.find((v) => v.isPopular);
  if (popular) return popular;
  return [...variants].sort((a, b) => a.sortOrder - b.sortOrder)[0] ?? null;
}

/**
 * POST /v1/commerce/order — create a pending checkout order (jwt.optional).
 * Cookie session authenticates buyers; guests get a `checkoutToken`.
 */
export function createCommerceOrder(
  variantId: number,
): Promise<ApiCommerceOrder> {
  return request<ApiCommerceOrder>("/v1/commerce/order", {
    method: "POST",
    body: JSON.stringify({ variantId }),
  });
}

/**
 * POST /v1/commerce/order/{id}/pay — start payment for a pending order (jwt.optional).
 * Guests must pass the `checkoutToken` from order create.
 * Optional `metadata` (UTM / click ids) is forwarded to Stripe; do not clear
 * local attribution storage after calling this.
 */
export function payCommerceOrder(
  orderId: number,
  provider: CommercePaymentProvider = "stripe",
  checkoutToken?: string | null,
  metadata?: CommercePaymentMetadata | null,
): Promise<ApiCommerceOrderPaymentResult> {
  const cleanedMetadata = sanitizeCommercePaymentMetadata(metadata);

  return request<ApiCommerceOrderPaymentResult>(
    `/v1/commerce/order/${orderId}/pay`,
    {
      method: "POST",
      body: JSON.stringify({
        provider,
        ...(checkoutToken ? { checkoutToken } : {}),
        ...(cleanedMetadata ? { metadata: cleanedMetadata } : {}),
      }),
    },
  );
}

/** Result from POST /v1/commerce/checkout/complete. */
export interface ApiCommerceCheckoutComplete {
  order: ApiCommerceOrder;
  accountCreated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  tokenType: "Bearer" | null;
  expiresIn: number | null;
}

/**
 * POST /v1/commerce/checkout/complete — finalize guest checkout via Stripe
 * `sessionId` (from success redirect `?session_id=`). Returns JWT when a new
 * account was provisioned for that order.
 */
export function completeCommerceCheckout(
  sessionId: string,
  email?: string | null,
): Promise<ApiCommerceCheckoutComplete> {
  return request<ApiCommerceCheckoutComplete>("/v1/commerce/checkout/complete", {
    method: "POST",
    body: JSON.stringify({
      sessionId,
      ...(email ? { email } : {}),
    }),
  });
}

/** Format a commerce amount with its ISO currency code for the UI locale. */
export function formatCommerceMoney(
  amount: number,
  currency: string,
  locale?: string,
): string {
  try {
    return new Intl.NumberFormat(locale || undefined, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (err) {
    console.error("Failed to format commerce money", err);
    return `${currency} ${amount}`;
  }
}
